import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useLessonSpeech
 * ---------------
 * Read a block of (already plain) text aloud using the browser's Web Speech
 * API. Returns a small player interface so the calling component stays purely
 * presentational:
 *
 *   const { supported, status, play, pause, resume, stop } =
 *       useLessonSpeech({ text, lang, rate });
 *
 *   status ∈ "idle" | "playing" | "paused"
 *
 * ── Upgrade seam ───────────────────────────────────────────────────────────
 * This hook intentionally hides *how* the audio is produced: it drives the free,
 * browser-native speechSynthesis engine. `useLessonAudio` is the premium layer
 * built on that seam — same { supported, status, play, pause, resume, stop }
 * shape, backed by pre-generated ElevenLabs clips in Cloud Storage, and it falls
 * back to this hook whenever REACT_APP_TTS_PROVIDER is unset or anything goes
 * wrong. `ListenButton` can't tell the two apart.
 * ───────────────────────────────────────────────────────────────────────────
 */

const isSupported = () =>
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

// Keep each utterance short. Chrome stops speaking a single long utterance after
// ~15s; splitting on sentence boundaries sidesteps that and keeps pause/resume
// responsive. Most lesson sentences fall well under this length.
const MAX_CHUNK = 200;

const chunkText = (text) => {
    if (!text) return [];
    const sentences =
        text.replace(/\s+/g, " ").match(/[^.!?。！？]+[.!?。！？]*\s*/g) || [text];
    const chunks = [];
    let current = "";
    for (const sentence of sentences) {
        if (current && (current + sentence).length > MAX_CHUNK) {
            chunks.push(current.trim());
            current = sentence;
        } else {
            current += sentence;
        }
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
};

// Best available voice for a BCP-47 locale: exact match, then same base
// language, then null (let the engine pick its default).
const pickVoice = (voices, lang) => {
    if (!voices || voices.length === 0) return null;
    const target = lang.toLowerCase();
    const base = target.split("-")[0];
    return (
        voices.find((v) => v.lang && v.lang.toLowerCase() === target) ||
        voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(base)) ||
        null
    );
};

export default function useLessonSpeech({ text, lang = "en-US", rate = 1 }) {
    const [supported] = useState(isSupported);
    const [status, setStatus] = useState("idle");

    // Latest values kept in refs so the queued-speech callbacks never close over
    // stale text/lang/voices.
    const textRef = useRef(text);
    const langRef = useRef(lang);
    const rateRef = useRef(rate);
    const voicesRef = useRef([]);
    const chunksRef = useRef([]);
    const indexRef = useRef(0);
    // Bumped every time playback is cancelled or restarted. Callbacks on an
    // utterance we cancelled still fire, so they check this before chaining —
    // otherwise a stale `onend` would queue the next chunk of a dead run on top
    // of the live one.
    const runRef = useRef(0);

    textRef.current = text;
    langRef.current = lang;
    rateRef.current = rate;

    // Voice list is populated asynchronously in most browsers (initially empty,
    // filled after the "voiceschanged" event).
    useEffect(() => {
        if (!supported) return undefined;
        const synth = window.speechSynthesis;
        const load = () => {
            voicesRef.current = synth.getVoices();
        };
        load();
        synth.addEventListener?.("voiceschanged", load);
        return () => synth.removeEventListener?.("voiceschanged", load);
    }, [supported]);

    const stop = useCallback(() => {
        if (!supported) return;
        runRef.current += 1;
        window.speechSynthesis.cancel();
        chunksRef.current = [];
        indexRef.current = 0;
        setStatus("idle");
    }, [supported]);

    // Speak the chunk at the current index, then chain to the next on completion.
    const speakNext = useCallback(() => {
        const synth = window.speechSynthesis;
        const i = indexRef.current;
        const run = runRef.current;
        const chunks = chunksRef.current;
        if (i >= chunks.length) {
            setStatus("idle");
            return;
        }
        const utter = new SpeechSynthesisUtterance(chunks[i]);
        utter.lang = langRef.current;
        // The engine locks an utterance's rate in when it starts speaking, so
        // this is where a speed change takes effect — see the effect below for
        // how a change mid-lesson is applied to the chunk already in progress.
        utter.rate = rateRef.current;
        const voice = pickVoice(voicesRef.current, langRef.current);
        if (voice) utter.voice = voice;
        utter.onend = () => {
            if (run !== runRef.current) return;
            indexRef.current = i + 1;
            if (indexRef.current < chunksRef.current.length) {
                speakNext();
            } else {
                setStatus("idle");
            }
        };
        utter.onerror = () => {
            if (run !== runRef.current) return;
            setStatus("idle");
        };
        synth.speak(utter);
    }, []);

    const play = useCallback(() => {
        if (!supported) return;
        const synth = window.speechSynthesis;
        // Already paused → resume in place.
        if (status === "paused") {
            synth.resume();
            setStatus("playing");
            return;
        }
        const speakable = (textRef.current || "").trim();
        if (!speakable) return;
        runRef.current += 1;
        synth.cancel();
        chunksRef.current = chunkText(speakable);
        indexRef.current = 0;
        if (chunksRef.current.length === 0) return;
        setStatus("playing");
        speakNext();
    }, [supported, status, speakNext]);

    const pause = useCallback(() => {
        if (!supported || status !== "playing") return;
        window.speechSynthesis.pause();
        setStatus("paused");
    }, [supported, status]);

    const resume = useCallback(() => {
        if (!supported || status !== "paused") return;
        window.speechSynthesis.resume();
        setStatus("playing");
    }, [supported, status]);

    // A speed change can't be applied to an utterance that is already speaking,
    // so re-speak the current chunk at the new rate. Only `rate` is a dependency
    // on purpose: reacting to `status` too would restart playback every time it
    // changed. The chunk restarts from its start, which is a sentence or two at
    // most.
    useEffect(() => {
        if (!supported || status !== "playing") return;
        runRef.current += 1;
        window.speechSynthesis.cancel();
        speakNext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rate]);

    // Stop playback when the lesson text changes (navigated to a new lesson).
    useEffect(() => {
        stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    // Safety net: never leave the engine speaking after unmount.
    useEffect(() => {
        return () => {
            if (supported) window.speechSynthesis.cancel();
        };
    }, [supported]);

    return { supported, status, play, pause, resume, stop };
}
